import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Library, Search, Plus } from "lucide-react";
import { API } from "../Sidebar";

/**
 * Read-only cross-site library of all tools. Useful for the user to scan
 * what's already in his Affiliate Hub without diving into each site.
 */
export const AffiliateToolsLibraryPage = () => {
  const [sites, setSites] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [frOnly, setFrOnly] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const sRes = await axios.get(`${API}/affiliate/sites`);
      setSites(sRes.data);
      const perSite = await Promise.all(
        sRes.data.map(async (s) => {
          const [tRes, cRes] = await Promise.all([
            axios.get(`${API}/affiliate/sites/${s.id}/tools`),
            axios.get(`${API}/affiliate/sites/${s.id}/categories`),
          ]);
          const catById = Object.fromEntries(cRes.data.map((c) => [c.id, c]));
          return tRes.data.map((t) => ({
            ...t,
            _siteName: s.name,
            _siteSlug: s.slug,
            _categoryName: catById[t.category_id]?.name || "—",
            _categories: cRes.data, // for the add-tool modal
          }));
        })
      );
      setTools(perSite.flat());
    } catch (e) {
      toast.error("Échec du chargement de la bibliothèque");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = tools.filter((t) => {
    if (siteFilter && t.site_id !== siteFilter) return false;
    if (frOnly && t.country !== "France") return false;
    if (q) {
      const needle = q.toLowerCase();
      return (
        t.name.toLowerCase().includes(needle) ||
        (t.short_description || "").toLowerCase().includes(needle) ||
        (t.company || "").toLowerCase().includes(needle)
      );
    }
    return true;
  });

  return (
    <div data-testid="affiliate-tools-library-page">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Library className="w-5 h-5 text-orange-500" />
            <h2
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              Tools Library
            </h2>
          </div>
          <p className="text-slate-600">
            {tools.length} outil{tools.length === 1 ? "" : "s"} dans la
            bibliothèque · {tools.filter((t) => t.country === "France").length}{" "}
            alternative{tools.filter((t) => t.country === "France").length === 1 ? "" : "s"} 🇫🇷
          </p>
        </div>
        <Button
          onClick={() => setShowAdd(true)}
          disabled={sites.length === 0}
          data-testid="library-add-tool-btn"
          className="bg-orange-500 hover:bg-orange-600"
        >
          <Plus className="w-4 h-4 mr-2" /> Ajouter un outil
        </Button>
      </div>
      <div className="mb-6" />

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-col md:flex-row gap-2">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input
              data-testid="tools-library-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher (nom, société, description)…"
              className="pl-9"
            />
          </div>
          <select
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            data-testid="tools-library-site-filter"
            className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm md:w-64"
          >
            <option value="">Tous les sites</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setFrOnly((v) => !v)}
            data-testid="tools-library-fr-toggle"
            className={`h-10 px-4 rounded-md border text-sm font-medium transition-colors flex items-center gap-1.5 ${
              frOnly
                ? "bg-blue-500 border-blue-500 text-white"
                : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            🇫🇷 FR only
          </button>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-slate-500 py-8 text-center">Chargement…</p>
      ) : filtered.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Library className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            {sites.length === 0 ? (
              <>
                <p className="text-slate-600 mb-1">Aucun site Affiliate.</p>
                <p className="text-slate-500 text-sm">
                  Créez d'abord un site dans My Sites pour ajouter des outils.
                </p>
                <Link to="/admin/affiliate/builder">
                  <Button className="mt-4 bg-orange-500 hover:bg-orange-600">
                    Créer un site Affiliate
                  </Button>
                </Link>
              </>
            ) : tools.length === 0 ? (
              <>
                <p className="text-slate-600 mb-1">
                  Aucun outil dans vos sites.
                </p>
                <p className="text-slate-500 text-sm">
                  Ajoutez-en un manuellement, ou utilisez les packs de seed
                  dans la vue d'un site.
                </p>
                <Button
                  onClick={() => setShowAdd(true)}
                  className="mt-4 bg-orange-500 hover:bg-orange-600"
                  data-testid="library-empty-add-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Ajouter un outil
                </Button>
              </>
            ) : (
              <p className="text-slate-600">
                Aucun outil ne correspond à votre recherche / filtre.
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-slate-600">
                  <th className="px-4 py-3 font-semibold">Outil</th>
                  <th className="px-4 py-3 font-semibold">Site</th>
                  <th className="px-4 py-3 font-semibold">Catégorie</th>
                  <th className="px-4 py-3 font-semibold">Prix</th>
                  <th className="px-4 py-3 font-semibold">Note</th>
                  <th className="px-4 py-3 font-semibold">Statut</th>
                  <th className="px-4 py-3 font-semibold text-right">Lien</th>
                </tr>
              </thead>
              <tbody data-testid="tools-library-tbody">
                {filtered.map((t) => (
                  <tr
                    key={t.id}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60"
                    data-testid={`library-tool-${t.slug}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 flex items-center gap-1.5">
                        {t.name}
                        {t.country === "France" && (
                          <span
                            title="Alternative française"
                            aria-label="Alternative française"
                            data-testid={`library-fr-flag-${t.slug}`}
                            className="text-base leading-none"
                          >
                            🇫🇷
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        {t.company || "—"}
                      </div>
                      {t.short_description && (
                        <div className="text-xs text-slate-500 mt-1 max-w-md truncate">
                          {t.short_description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/affiliate/sites/${t.site_id}`}
                        className="text-orange-600 hover:underline"
                      >
                        {t._siteName}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <Badge variant="outline" className="font-normal">
                        {t._categoryName || "—"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {t.has_free_plan && (
                        <Badge
                          variant="secondary"
                          className="mr-1 bg-emerald-100 text-emerald-700"
                        >
                          Gratuit
                        </Badge>
                      )}
                      {t.price_starting_at
                        ? `${t.price_starting_at} ${t.currency || ""}`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {t.rating_overall ? `${t.rating_overall}/5` : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant="outline"
                        className={
                          t.status === "active"
                            ? "border-emerald-300 text-emerald-700"
                            : t.status === "archived"
                            ? "border-slate-300 text-slate-500"
                            : "border-amber-300 text-amber-700"
                        }
                      >
                        {t.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/admin/affiliate/sites/${t.site_id}`}>
                        <Button variant="outline" size="sm">
                          Ouvrir le site
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {showAdd && (
        <AddToolModal
          sites={sites}
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            fetchAll();
          }}
        />
      )}
    </div>
  );
};


const AddToolModal = ({ sites, onClose, onCreated }) => {
  const [siteId, setSiteId] = useState(sites[0]?.id || "");
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    category_id: "",
    name: "",
    short_description: "",
    website_url: "",
    affiliate_url: "",
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!siteId) {
      setCategories([]);
      return;
    }
    setLoadingCats(true);
    axios
      .get(`${API}/affiliate/sites/${siteId}/categories`)
      .then((r) => {
        setCategories(r.data);
        setForm((f) => ({ ...f, category_id: r.data[0]?.id || "" }));
      })
      .catch(() => toast.error("Échec du chargement des catégories"))
      .finally(() => setLoadingCats(false));
  }, [siteId]);

  const submit = async () => {
    if (!siteId || !form.category_id || !form.name.trim()) {
      toast.error("Site, catégorie et nom sont requis");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/affiliate/tools`, {
        site_id: siteId,
        category_id: form.category_id,
        name: form.name.trim(),
        short_description: form.short_description.trim() || undefined,
        website_url: form.website_url.trim() || undefined,
        affiliate_url: form.affiliate_url.trim() || undefined,
      });
      toast.success(`Outil "${form.name}" créé`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la création");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-16 pb-10 overflow-auto"
      onClick={onClose}
      data-testid="add-tool-modal"
    >
      <Card
        className="w-full max-w-xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-900">
              Ajouter un outil à la bibliothèque
            </h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              Fermer
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Site *</Label>
              <select
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                data-testid="add-tool-site"
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
              >
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Catégorie *</Label>
              <select
                value={form.category_id}
                onChange={(e) => upd("category_id", e.target.value)}
                data-testid="add-tool-category"
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                disabled={loadingCats || categories.length === 0}
              >
                {categories.length === 0 ? (
                  <option value="">
                    {loadingCats ? "Chargement…" : "Aucune catégorie"}
                  </option>
                ) : (
                  categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-600">Nom de l'outil *</Label>
            <Input
              data-testid="add-tool-name"
              value={form.name}
              onChange={(e) => upd("name", e.target.value)}
              placeholder="Ex: Notion AI"
              autoFocus
            />
          </div>

          <div>
            <Label className="text-xs text-slate-600">
              Description courte (≤ 120 c.)
            </Label>
            <Input
              data-testid="add-tool-description"
              value={form.short_description}
              onChange={(e) => upd("short_description", e.target.value)}
              placeholder="L'assistant IA intégré à Notion."
              maxLength={120}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">URL site officiel</Label>
              <Input
                data-testid="add-tool-website"
                value={form.website_url}
                onChange={(e) => upd("website_url", e.target.value)}
                placeholder="https://…"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">
                URL affiliée (trackée)
              </Label>
              <Input
                data-testid="add-tool-affiliate"
                value={form.affiliate_url}
                onChange={(e) => upd("affiliate_url", e.target.value)}
                placeholder="https://…?ref=…"
              />
            </div>
          </div>

          <p className="text-xs text-slate-500 italic">
            Vous compléterez verdicts, pros/cons, prix et features après création
            depuis la vue détail du site.
          </p>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="add-tool-submit"
              className="bg-orange-500 hover:bg-orange-600"
            >
              {submitting ? "Création…" : "Créer l'outil"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
